<template>
  <v-container fluid>
    <div class="d-flex flex-wrap align-center mb-1">
      <h2 class="text-xl font-weight-medium mr-3">
        出勤の申告
      </h2>

      <span class="check-in-settings__admin text-xs">
        <v-icon
          x-small
          class="mr-1"
        >
          {{ icons.mdiLock }}
        </v-icon>adminのみ
      </span>
    </div>

    <p class="text-sm mb-6">
      {{ lede }}
    </p>

    <div
      v-if="isLoading"
      class="text-center py-8"
    >
      <v-progress-circular
        indeterminate
        color="primary"
      />
    </div>

    <v-alert
      v-else-if="loadError"
      type="error"
      outlined
    >
      設定を読み込めませんでした。リロードしてください。
    </v-alert>

    <template v-else>
      <time-rule
        :rule="settings.rule"
        :items="settings.items"
        :grouped="settings.grouped"
        :customized="settings.ruleSource === 'club'"
        :saving="saving"
        @save="run(() => api.updateRule($event), '出勤時刻の計算を保存しました')"
        @inherit="confirmInherit('rule')"
      />

      <declaration-items
        :items="settings.items"
        :presets="settings.presets"
        :grouped="settings.grouped"
        :customized="settings.itemsSource === 'club'"
        :time-rule-enabled="!!(settings.rule && settings.rule.enabled)"
        :saving="saving"
        @customize="run(() => api.customizeItems(), 'この店舗だけの申告項目にしました')"
        @inherit="confirmInherit('items')"
        @create="run(() => api.createItem($event), '申告項目を追加しました')"
        @update="run(() => api.updateItem($event.id, $event.item), '申告項目を保存しました')"
        @delete="run(() => api.deleteItem($event), '申告項目を削除しました')"
        @reorder="run(() => api.reorderItems($event))"
      />
    </template>

    <v-dialog
      :value="!!inheriting"
      width="460"
      @input="inheriting = null"
    >
      <v-card>
        <v-card-title>グループの設定に戻しますか</v-card-title>

        <v-card-text>
          {{ inheriting === 'items' ? 'この店舗だけの申告項目を消して、グループの設定に戻します。' : 'この店舗だけの出勤時刻の計算を消して、グループの設定に戻します。' }}
          すでに打刻したタイムカードは変わりません。
        </v-card-text>

        <v-card-actions>
          <v-spacer />

          <v-btn
            color="primary"
            :ripple="false"
            @click="inherit"
          >
            戻す
          </v-btn>

          <v-btn
            text
            color="secondary"
            :ripple="false"
            @click="inheriting = null"
          >
            キャンセル
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import {
  ref,
  computed,
  getCurrentInstance,
} from '@vue/composition-api'
import { mdiLock } from '@mdi/js'
import CheckInSettingsApi from '@/api/admin/CheckInSettings'
import useCurrentData from '@/views/composable/useCurrentData'
import DeclarationItems from './components/DeclarationItems.vue'
import TimeRule from './components/TimeRule.vue'

// 出勤の申告の設定(その店舗の個別)。時給に直結するため admin だけが開ける。
// 今の「店舗」設定の画面(タイムカード打刻設定)とは別の画面
export default {
  components: {
    DeclarationItems,
    TimeRule,
  },
  setup() {
    const vm = getCurrentInstance().proxy
    const { currentClub } = useCurrentData()

    const settings = ref({
      grouped: false,
      groupName: null,
      ruleSource: 'none',
      itemsSource: 'none',
      rule: null,
      items: [],
      presets: [],
    })
    const isLoading = ref(true)
    const loadError = ref(false)
    const saving = ref(false)
    const inheriting = ref(null)

    const lede = computed(() => {
      const name = currentClub.value?.name || 'この店舗'
      if (!settings.value.grouped) return `${name}の設定です。`

      const customized = settings.value.ruleSource === 'club' || settings.value.itemsSource === 'club'

      return customized
        ? `${name}の設定です。一部をこの店舗だけの設定にしています。`
        : `${name}の設定です。今はグループの設定がそのまま当てはまっています。`
    })

    const load = async () => {
      try {
        const res = await CheckInSettingsApi.getCheckInSettings()
        settings.value = res.data.checkInSettings
      } catch (error) {
        loadError.value = true
      } finally {
        isLoading.value = false
      }
    }

    // どの操作も、保存後の設定がそのまま返ってくる
    const run = async (request, message = null) => {
      saving.value = true
      try {
        const res = await request()
        settings.value = res.data.checkInSettings
        if (message) vm.$toast.success(message)
      } catch (error) {
        const messages = error?.response?.data?.message
        vm.$toast.error([].concat(messages || '保存に失敗しました').join(' '))
      } finally {
        saving.value = false
      }
    }

    const confirmInherit = target => {
      inheriting.value = target
    }

    const inherit = () => {
      const target = inheriting.value
      inheriting.value = null

      return target === 'items'
        ? run(() => CheckInSettingsApi.inheritItems(), '申告項目をグループの設定に戻しました')
        : run(() => CheckInSettingsApi.inheritRule(), '出勤時刻の計算をグループの設定に戻しました')
    }

    load()

    return {
      settings,
      isLoading,
      loadError,
      saving,
      inheriting,
      lede,
      api: CheckInSettingsApi,

      run,
      confirmInherit,
      inherit,

      icons: {
        mdiLock,
      },
    }
  },
}
</script>

<style scoped>
.check-in-settings__admin {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 2px 10px;
  background: rgba(58, 53, 65, 0.08);
  color: rgba(58, 53, 65, 0.68);
  font-weight: 600;
}
</style>
